import { useCallback, useEffect, useRef, useState } from "react";

export type GuideState = {
  hidden: boolean;
  dismissedCards: string[];
  /** Classes whose roster the teacher printed or downloaded. */
  rosterShared: string[];
  openedCanvas: boolean;
};

const EMPTY: GuideState = {
  hidden: false,
  dismissedCards: [],
  rosterShared: [],
  openedCanvas: false,
};

const GUIDE_EVENT = "canvaschool:guide";

const storageKey = (userId: string) => `canvaschool:guide:${userId}`;

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

export const readGuideState = (userId: string): GuideState => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) {
      return EMPTY;
    }
    const data = JSON.parse(raw) as Record<string, unknown> | null;
    if (!data || typeof data !== "object") {
      return EMPTY;
    }
    return {
      hidden: data.hidden === true,
      dismissedCards: strings(data.dismissedCards),
      rosterShared: strings(data.rosterShared),
      openedCanvas: data.openedCanvas === true,
    };
  } catch {
    return EMPTY;
  }
};

const storageWorks = (userId: string): boolean => {
  try {
    window.localStorage.getItem(storageKey(userId));
    return true;
  } catch {
    return false;
  }
};

const writeGuideState = (userId: string, state: GuideState) => {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // Without storage the guide still works until the page is reloaded.
  }
};

/** Guide progress for one account, saved on every change. */
export const useGuideState = (userId: string) => {
  const [state, setState] = useState(() => readGuideState(userId));
  const stateRef = useRef(state);

  // Several components keep a copy; a change in one is announced to the rest
  // with the new state itself, so it also reaches them when storage is blocked.
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (
        event as CustomEvent<{ userId: string; state: GuideState }>
      ).detail;
      if (detail?.userId === userId) {
        stateRef.current = detail.state;
        setState(detail.state);
      }
    };
    window.addEventListener(GUIDE_EVENT, onChange);
    return () => window.removeEventListener(GUIDE_EVENT, onChange);
  }, [userId]);

  const update = useCallback(
    (change: (current: GuideState) => GuideState) => {
      // Build on what is saved, not on a stale copy, unless storage is
      // unavailable.
      const base = storageWorks(userId)
        ? readGuideState(userId)
        : stateRef.current;
      const next = change(base);
      stateRef.current = next;
      setState(next);
      if (next !== base) {
        writeGuideState(userId, next);
        window.dispatchEvent(
          new CustomEvent(GUIDE_EVENT, { detail: { userId, state: next } }),
        );
      }
    },
    [userId],
  );
  return {
    state,
    setHidden: (hidden: boolean) => update((s) => ({ ...s, hidden })),
    dismissCard: (id: string) =>
      update((s) =>
        s.dismissedCards.includes(id)
          ? s
          : { ...s, dismissedCards: [...s.dismissedCards, id] },
      ),
    markRosterShared: (classId: string) =>
      update((s) =>
        s.rosterShared.includes(classId)
          ? s
          : { ...s, rosterShared: [...s.rosterShared, classId] },
      ),
    markCanvasOpened: () =>
      update((s) => (s.openedCanvas ? s : { ...s, openedCanvas: true })),
  };
};
